import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";

const faqData = [
  {
    question: "What types of companies do you typically work with?",
    answer: "We specialize in working with high-growth startups, particularly those in the technology sector preparing for Series A and beyond. We also serve established companies seeking fractional CFO services and high-net-worth individuals requiring sophisticated tax planning.",
  },
  {
    question: "How does your fractional CFO service work?",
    answer: "Our fractional CFO service provides you with experienced financial leadership without the cost of a full-time hire. We typically engage for 15-30 hours per month, handling financial strategy, board reporting, investor relations, and cash flow management. The engagement is flexible and scales with your needs.",
  },
  {
    question: "What's included in your Startup Advisory package?",
    answer: "Our Startup Advisory includes fundraising strategy development, financial model building, pitch deck review and optimization, investor introduction facilitation, term sheet negotiation support, and post-funding financial setup. We tailor each engagement to your specific stage and goals.",
  },
  {
    question: "How do you approach tax planning for founders?",
    answer: "We take a proactive, year-round approach to tax planning. This includes optimizing equity compensation structures, managing QSBS eligibility, implementing charitable giving strategies, and coordinating with estate planning. Our goal is to minimize tax burden while maximizing wealth preservation.",
  },
  {
    question: "What is your engagement process?",
    answer: "We begin with a complimentary discovery call to understand your needs. If there's a fit, we conduct a comprehensive financial assessment, then present a customized engagement proposal. Our onboarding process typically takes 2-3 weeks, after which we move into active advisory mode.",
  },
  {
    question: "Do you work with companies outside the US?",
    answer: "Yes, we work with companies globally, particularly those with US operations or investors. We have expertise in cross-border tax implications, international structuring, and helping non-US founders navigate the American financial and regulatory landscape.",
  },
];

const FAQItem = ({ item, isOpen, onToggle, index }) => {
  return (
    <div
      className={`bg-[#121212] rounded-xl border transition-colors ${
        isOpen ? "border-[#00C896]/20" : "border-white/5"
      }`}
    >
      <button
        data-testid={`faq-trigger-${index}`}
        onClick={onToggle}
        className="w-full flex items-center justify-between p-5 md:p-6 text-left"
      >
        <span className="text-base md:text-lg font-medium text-white pr-4">
          {item.question}
        </span>
        <span className={`text-[#00C896] transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}>
          {isOpen ? <Minus size={20} /> : <Plus size={20} />}
        </span>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="px-5 md:px-6 pb-5 md:pb-6 text-slate-400 leading-relaxed">
              {item.answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const handleToggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section data-testid="faq-section" className="py-24 md:py-32 bg-[#0A0A0A]">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-xs font-mono tracking-wider uppercase text-[#00C896]/80 mb-4 block">
            FAQ
          </span>
          <h2
            className="text-4xl md:text-5xl font-semibold tracking-tight text-white mb-6"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-slate-400 leading-relaxed">
            Everything you need to know about our services and engagement process.
          </p>
        </motion.div>

        {/* FAQ Items */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="space-y-4"
        >
          {faqData.map((faq, index) => (
            <FAQItem
              key={index}
              item={faq}
              index={index}
              isOpen={openIndex === index}
              onToggle={() => handleToggle(index)}
            />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default FAQSection;
